import {
  artifactExtension,
  getArtifactFileMeta,
  type ArtifactFileMeta,
} from '@cowbell/ui';
import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';

/** Extension to look up when a filename carries none of its own. */
const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/msword': 'doc',
  'text/plain': 'txt',
  'application/zip': 'zip',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/**
 * Icon, type name and tint for an attachment. The filename's extension
 * decides; the content type only fills in when the name has no extension.
 */
export const attachmentFileMeta = (
  attachment: MessageInfoAttachment
): ArtifactFileMeta => {
  const name = attachment.docName ?? '';
  if (artifactExtension(name)) return getArtifactFileMeta(name);

  const extension = CONTENT_TYPE_EXTENSIONS[attachment.docContentType ?? ''];
  return getArtifactFileMeta(extension ? `file.${extension}` : name);
};

/** `1-2` -> `pages 1–2`, `3` -> `page 3`. */
export const pageRangeLabel = (pageRange: string): string => {
  const pages = pageRange.replace('-', '–');
  return pageRange.includes('-') ? `pages ${pages}` : `page ${pages}`;
};

/**
 * The chip's second line: `PDF · 3 KB`, or just `PDF` without a size. An
 * invoice slice adds its source pages: `PDF · 3 KB · pages 1–2`.
 */
export const attachmentMetaLabel = (
  typeName: string,
  sizeLabel: string,
  pageRange?: string
): string =>
  [typeName, sizeLabel, pageRange && pageRangeLabel(pageRange)]
    .filter(Boolean)
    .join(' · ');
